import React, {useEffect, useRef, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {playerKeys} from '@/features/players/queries';
import {WyscoutAnalyticsDashboard, WyscoutAnalyticsSyncState, wyscoutAnalyticsAPI} from '@/features/players/wyscoutAnalyticsApi';
import {getV2Error, hasCapability} from '@/services/api/v2';
import LoadingStatus from '@/components/ui/LoadingStatus';

interface Props {
  workspaceId: string;
  playerId: string;
  data: WyscoutAnalyticsDashboard;
  isFetching: boolean;
  refreshFailed: boolean;
  dataUpdatedAt: number;
  onRefresh: () => void;
}

const activeStatuses = new Set(['queued', 'running']);
const retryableStatuses = new Set(['partial', 'failed']);
const terminalStatuses = new Set(['partial', 'failed', 'complete']);

const timestamp = (value: string | null): number | null => {
  if (value === null) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const mergeSyncState = (
  current: WyscoutAnalyticsSyncState,
  incoming: WyscoutAnalyticsSyncState,
  newlyCreated: boolean,
): WyscoutAnalyticsSyncState => {
  if (current.runId === null) return incoming;
  if (incoming.runId === null) return current;
  if (current.runId !== incoming.runId) {
    const currentCreated = timestamp(current.createdAt);
    const incomingCreated = timestamp(incoming.createdAt);
    if (incomingCreated !== null && (currentCreated === null || incomingCreated > currentCreated)) return incoming;
    if (currentCreated !== null && (incomingCreated === null || incomingCreated < currentCreated)) return current;
    if (newlyCreated && terminalStatuses.has(current.status) && activeStatuses.has(incoming.status)) return incoming;
    const currentUpdated = timestamp(current.updatedAt);
    const incomingUpdated = timestamp(incoming.updatedAt);
    return incomingUpdated !== null && (currentUpdated === null || incomingUpdated > currentUpdated) ? incoming : current;
  }
  if (terminalStatuses.has(current.status) && activeStatuses.has(incoming.status)) return current;
  const currentUpdated = timestamp(current.updatedAt);
  const incomingUpdated = timestamp(incoming.updatedAt);
  if (incomingUpdated !== null && currentUpdated !== null && incomingUpdated !== currentUpdated) {
    return incomingUpdated > currentUpdated ? incoming : current;
  }
  if (incomingUpdated !== null && currentUpdated === null) return incoming;
  if (currentUpdated !== null && incomingUpdated === null) return current;
  if (terminalStatuses.has(incoming.status) && activeStatuses.has(current.status)) return incoming;
  const currentProgress = current.succeeded + current.failed;
  const incomingProgress = incoming.succeeded + incoming.failed;
  if (incomingProgress > currentProgress || incoming.attempts > current.attempts) return incoming;
  if (current.status === 'queued' && incoming.status === 'running') return incoming;
  return current;
};

const syncSignature = (state: WyscoutAnalyticsSyncState): string => JSON.stringify([
  state.runId, state.status, state.selected, state.succeeded, state.failed, state.attempts,
  state.currentMatchId, state.createdAt, state.updatedAt, state.completedAt,
]);

const AnalyticsSyncPanel: React.FC<Props> = ({
  workspaceId, playerId, data, isFetching, refreshFailed, dataUpdatedAt, onRefresh,
}) => {
  const queryClient = useQueryClient();
  const controller = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const inFlight = useRef(false);
  const syncRef = useRef(data.sync);
  syncRef.current = data.sync;
  const errorSyncSignature = useRef<string | null>(null);
  const [actionError, setActionError] = useState(false);
  const status = data.sync.status;
  const active = activeStatuses.has(status);
  const retryable = retryableStatuses.has(status);
  const permitted = data.analyticsCapabilities.canSynchronize && hasCapability(data.capabilities, 'wyscout.sync');
  const canStart = (status === 'missing' || status === 'complete') && permitted;
  const canRetry = retryable && data.analyticsCapabilities.canRetrySync && permitted;
  const visible = canStart || active || retryable || status === 'complete';
  const processed = Math.min(data.sync.selected, data.sync.succeeded + data.sync.failed);
  const percentage = data.sync.selected > 0 ? Math.round(processed / data.sync.selected * 100) : 0;
  const currentMatch = data.filterOptions.matches.find(match => match.id === data.sync.currentMatchId);

  const mutation = useMutation({
    mutationFn: async (request: {generation: number; syncSignature: string}) => {
      const requestController = new AbortController();
      controller.current = requestController;
      const response = await wyscoutAnalyticsAPI.synchronize(workspaceId, playerId, requestController.signal);
      return {response, request, requestController};
    },
    onSuccess: ({response, request, requestController}) => {
      if (request.generation !== generation.current || requestController.signal.aborted) return;
      queryClient.setQueriesData<WyscoutAnalyticsDashboard>(
        {queryKey: [...playerKeys.detail(workspaceId, playerId), 'wyscout-analytics']},
        (current: WyscoutAnalyticsDashboard | undefined) => current
          ? {...current, sync: mergeSyncState(current.sync, response.sync, !response.reused)}
          : current,
      );
      void queryClient.invalidateQueries({queryKey: playerKeys.detail(workspaceId, playerId), exact: true});
      void queryClient.invalidateQueries({queryKey: [...playerKeys.detail(workspaceId, playerId), 'wyscout-analytics']});
    },
    onError: (error, request) => {
      if (request.generation !== generation.current || controller.current?.signal.aborted) return;
      if (getV2Error(error).code === 'analytics_current') {
        errorSyncSignature.current = null;
        setActionError(false);
        void Promise.all([
          queryClient.invalidateQueries({queryKey: playerKeys.detail(workspaceId, playerId), exact: true}),
          queryClient.invalidateQueries({queryKey: [...playerKeys.detail(workspaceId, playerId), 'wyscout-analytics']}),
        ]).catch(() => undefined);
        return;
      }
      const currentSignature = syncSignature(syncRef.current);
      if (currentSignature !== request.syncSignature) return;
      errorSyncSignature.current = currentSignature;
      setActionError(true);
    },
    onSettled: (_data, _error, request) => {
      if (request.generation !== generation.current) return;
      inFlight.current = false;
      controller.current = null;
    },
  });

  useEffect(() => {
    generation.current += 1;
    errorSyncSignature.current = null;
    setActionError(false);
    return () => {
      generation.current += 1;
      inFlight.current = false;
      controller.current?.abort();
      controller.current = null;
    };
  }, [playerId, workspaceId]);

  useEffect(() => {
    if (!actionError || errorSyncSignature.current === syncSignature(data.sync)) return;
    errorSyncSignature.current = null;
    setActionError(false);
  }, [actionError, data.sync]);

  if (!visible) return null;

  const start = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    errorSyncSignature.current = null;
    setActionError(false);
    mutation.mutate({generation: generation.current, syncSignature: syncSignature(data.sync)});
  };
  const statusLabel = status === 'queued'
    ? 'Analytics synchronization queued'
    : status === 'running'
      ? 'Synchronizing analytics'
      : status === 'partial'
        ? 'Analytics synchronization partially complete'
        : status === 'failed'
          ? 'Analytics synchronization failed'
          : status === 'complete'
            ? 'Analytics synchronization complete'
            : 'Analytics event data is incomplete';

  return <section aria-labelledby="analytics-sync-heading" className="rounded-lg bg-gray-50 p-4 ring-1 ring-gray-200 dark:bg-gray-950 dark:ring-gray-800">
    <h3 id="analytics-sync-heading" className="font-semibold text-gray-950 dark:text-white">Analytics synchronization</h3>
    {active
      ? <LoadingStatus label={statusLabel} className="mt-2" />
      : <div role="status" aria-live="polite" className="mt-2"><p className="font-medium text-gray-900 dark:text-gray-100">{statusLabel}</p></div>}
    {status === 'missing' && <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Synchronize missing event locations while keeping the analytics already available here.</p>}
    {status !== 'missing' && <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 dark:text-gray-200">
      <span>{data.sync.selected} selected</span>
      <span>{data.sync.succeeded} synchronized</span>
      <span>{data.sync.failed} failed</span>
      {data.sync.attempts > 0 && <span>{data.sync.attempts} {data.sync.attempts === 1 ? 'attempt' : 'attempts'}</span>}
    </div>}
    {status === 'running' && data.sync.selected > 0 && <div
      role="progressbar"
      aria-label={`${processed} of ${data.sync.selected} matches processed`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percentage}
      className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
    ><div className="h-full rounded-full bg-club-blue-600 transition-none motion-safe:transition-[width]" style={{width: `${percentage}%`}} /></div>}
    {status === 'running' && data.sync.currentMatchId !== null && <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Current match: {currentMatch ? `${currentMatch.opponent}, ${new Date(currentMatch.date).toLocaleDateString('en-GB')}` : 'In progress'}</p>}
    {active && <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Synchronization runs in background batches and continues if you leave this page. Available analytics remain usable.</p>}
    {refreshFailed && active && <p role="alert" className="mt-2 text-sm text-amber-800 dark:text-amber-200">Unable to refresh analytics status. Showing the last known progress.</p>}
    <div className="mt-3 flex flex-wrap items-center gap-3">
      {(canStart || canRetry) && <button
        type="button"
        disabled={mutation.isPending}
        aria-label={mutation.isPending ? (canRetry ? 'Retrying analytics' : 'Syncing analytics') : (canRetry ? 'Retry analytics' : 'Sync analytics')}
        onClick={start}
        className="min-h-11 rounded-md bg-club-blue-600 px-4 font-semibold text-white hover:bg-club-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:focus-visible:ring-offset-gray-950"
      >{mutation.isPending ? (canRetry ? 'Retrying analytics...' : 'Syncing analytics...') : (canRetry ? 'Retry analytics' : 'Sync analytics')}</button>}
      {active && <button
        type="button"
        disabled={isFetching}
        aria-label={isFetching ? 'Refreshing status' : 'Refresh status'}
        onClick={onRefresh}
        className="min-h-11 rounded-md border border-gray-300 px-3 font-semibold text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200"
      >{isFetching ? 'Refreshing...' : 'Refresh status'}</button>}
      {active && dataUpdatedAt > 0 && <p className="text-xs text-gray-500 dark:text-gray-400">Last checked {new Date(dataUpdatedAt).toLocaleTimeString('en-GB')}</p>}
    </div>
    {actionError && <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">Unable to start analytics synchronization. Please try again.</p>}
  </section>;
};

export default AnalyticsSyncPanel;
